import { Avatar, Button, Flex, Grid, Popover, Select, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { ArrowRight, RotateCw, X } from 'lucide-react';
import { useState } from 'react';

import {
  useCreateBillingTopUpCheckoutSession,
  useGetBillingOverview,
} from '../../../api/generated/api';
import { TextQuaternary, TextTertiary } from '../../../ui/components/text';
import { formatCredits, formatCreditsShort, TOP_UP_BLOCKED_COPY } from '../shared/billing-format';
import { formatDiscountedUsd } from '../shared/discount';
import { defaultPackId } from '../shared/packs';

import { DiscountedPrice } from './DiscountedPrice';

import type { GetAccountResponseDtoMembershipsItemOrganizationCredits } from '../../../api/generated/model/getAccountResponseDtoMembershipsItemOrganizationCredits';
import type { ReactNode } from 'react';

export type AddCreditsPopoverProps = {
  open: boolean;
  organizationId: string;
  credits: NonNullable<GetAccountResponseDtoMembershipsItemOrganizationCredits>;
  onClose: () => void;
  onOpenBilling: () => void;
  children: ReactNode;
};

export const AddCreditsPopover = ({
  open,
  organizationId,
  credits,
  onClose,
  onOpenBilling,
  children,
}: AddCreditsPopoverProps) => {
  const overviewQuery = useGetBillingOverview(organizationId, { query: { enabled: open } });
  const checkoutMutation = useCreateBillingTopUpCheckoutSession();
  const [picked, setPicked] = useState<string | null>(null);
  const isCompact = Grid.useBreakpoint().lg === false;

  const overview = overviewQuery.data?.data;
  const packs = overview?.topUp?.packs ?? [];
  const blocked = overview?.topUp?.blockedReason ?? null;
  const selected = packs.find(
    (pack) =>
      pack.priceId === (picked ?? defaultPackId(packs, overview?.autoRecharge?.pack.priceId)),
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      trigger="click"
      placement={isCompact ? 'top' : 'leftBottom'}
      align={{
        offset: isCompact ? [0, 0] : [-12, -14],
        overflow: { adjustY: false, shiftX: true, shiftY: true },
      }}
      styles={{
        container: {
          padding: 18,
          borderRadius: 20,
          border: '1px solid var(--ant-color-border-secondary)',
          boxShadow: 'var(--ant-box-shadow)',
        },
      }}
      content={
        <Flex
          vertical
          gap={16}
          data-testid="add-credits"
          style={{ width: isCompact ? 'min(340px, calc(100vw - 78px))' : 340 }}
        >
          <Flex align="center" justify="space-between" gap={8}>
            <Typography.Text strong>Add credits</Typography.Text>
            <Button
              type="text"
              shape="circle"
              size="small"
              icon={<X size={16} color="var(--ant-color-text-tertiary)" />}
              onClick={onClose}
            />
          </Flex>

          <Flex gap={8}>
            {[
              {
                label: credits.trial ? 'TRIAL' : 'MONTHLY',
                value: credits.balance - credits.topUpRemaining,
                icon: <RotateCw size={11} />,
                note: `${credits.trial ? 'Ends' : 'Resets'}${credits.renewsAt ? ` ${dayjs(credits.renewsAt).format('MMM D')}` : ''}`,
              },
              {
                label: 'TOP-UP',
                value: credits.topUpRemaining,
                icon: <ArrowRight size={11} />,
                note: 'Rolls over',
              },
            ].map((tile) => (
              <Flex
                key={tile.label}
                vertical
                gap={4}
                flex={1}
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: 'var(--ant-color-fill-alter)',
                  border: '1px solid var(--ant-color-split)',
                }}
              >
                <TextQuaternary style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}>
                  {tile.label}
                </TextQuaternary>
                <Typography.Text
                  strong
                  className="font-mono"
                  style={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatCredits(tile.value)}
                </Typography.Text>
                <Flex align="center" gap={5} style={{ color: 'var(--ant-color-text-tertiary)' }}>
                  {tile.icon}
                  <TextTertiary style={{ fontSize: 11 }}>{tile.note}</TextTertiary>
                </Flex>
              </Flex>
            ))}
          </Flex>

          <Flex
            vertical
            style={{
              borderTop: '1px solid var(--ant-color-split)',
              borderBottom: '1px solid var(--ant-color-split)',
            }}
          >
            {[
              {
                label: credits.trial ? 'Trial credits' : 'Monthly credits',
                note: credits.trial ? 'End with the trial' : 'Reset every month',
                icon: <RotateCw size={13} />,
                background: 'var(--ant-color-fill-tertiary)',
                color: 'var(--ant-color-text-secondary)',
              },
              {
                label: 'Top-up credits',
                note: 'Roll over, never expire',
                icon: <ArrowRight size={13} />,
                background: 'var(--ant-color-primary)',
                color: 'var(--ant-color-text-light-solid)',
              },
            ].map((row, index) => (
              <Flex
                key={row.label}
                align="center"
                wrap
                gap="4px 10px"
                style={{
                  padding: '10px 0',
                  borderBottom: index === 0 ? '1px solid var(--ant-color-split)' : undefined,
                }}
              >
                <Avatar
                  size={26}
                  icon={row.icon}
                  style={{ background: row.background, color: row.color }}
                />
                <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>
                  {row.label}
                </Typography.Text>
                <TextTertiary style={{ marginLeft: 'auto', fontSize: 12 }}>{row.note}</TextTertiary>
              </Flex>
            ))}
          </Flex>

          <Flex vertical gap={8}>
            <Flex align="center" gap={8}>
              <Select
                value={selected?.priceId}
                onChange={setPicked}
                loading={overviewQuery.isLoading}
                disabled={!selected || blocked !== null}
                placeholder="No packs available"
                data-testid="add-credits-pack"
                options={packs.map((pack) => ({
                  value: pack.priceId,
                  label: `${formatCreditsShort(pack.credits)} credits`,
                }))}
                style={{
                  flex: 1,
                  height: 35,
                  fontSize: 13,
                  fontWeight: 500,
                  background: 'var(--ant-color-fill-alter)',
                }}
                styles={{ root: { borderRadius: 10 } }}
              />
              <DiscountedPrice
                cents={selected?.priceCents}
                discountedCents={selected?.discountedPriceCents}
                className="font-mono"
                style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              />
            </Flex>
            {blocked && (
              <TextTertiary style={{ fontSize: 12 }}>{TOP_UP_BLOCKED_COPY[blocked]}</TextTertiary>
            )}
            {blocked ? (
              <Button
                type="primary"
                onClick={onOpenBilling}
                style={{ height: 36, padding: '10px 16px', fontSize: 13, fontWeight: 600 }}
              >
                Open billing
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={!selected}
                loading={checkoutMutation.isPending}
                data-testid="add-credits-buy"
                onClick={() =>
                  selected &&
                  checkoutMutation.mutate(
                    { organizationId, data: { priceId: selected.priceId } },
                    {
                      onSuccess: (result) => window.location.assign(result.data.url),
                      onError: () => message.error("Couldn't start checkout"),
                    },
                  )
                }
                style={{ height: 36, padding: '10px 16px', fontSize: 13, fontWeight: 600 }}
              >
                {selected ? `Buy for ${formatDiscountedUsd(selected.priceCents, selected.discountedPriceCents)}` : 'Buy credits'}
              </Button>
            )}
          </Flex>
        </Flex>
      }
    >
      {children}
    </Popover>
  );
};
